import clsx from 'clsx';
import { useCallback, useState } from 'react';

import { FormElementText } from '~components/generic/editor/FormElementText';
import { default as useLabels } from '~hooks/useLabels';
import { IEntityComponentProps } from '~interfaces/props';
import { ContactForm } from '~modules/jsm-contact/components/ContactForm';
import { JsmContactConfig } from '~modules/jsm-contact/model/Config';
import { default as JsmContactConfigFactory } from '~modules/jsm-contact/model/ConfigFactory';
import { createPortalBaseUrl } from '~modules/jsm-contact/shared/helpers';
import { getModuleUrlIdFromData } from '~services/location-helpers';

export default function ModuleJsmContact({ data, editing, className, onChange }: IEntityComponentProps) {
  // labels
  const {
    hlJsmContact,

    labelModuleAttributeJsmContactDataKey,
    placeholderModuleAttributeJsmContactDataKey,
    msgModuleAttributeJsmContactDataKeyHelp,
    msgModuleAttributeJsmContactDataKeyError,

    labelModuleAttributeJsmContactPortalBaseUrl,
    placeholderModuleAttributeJsmContactPortalBaseUrl,
    msgModuleAttributeJsmContactPortalBaseUrlHelp,
    msgModuleAttributeJsmContactPortalBaseUrlError,

    labelModuleAttributeJsmContactRequestTypeId,
    placeholderModuleAttributeJsmContactRequestTypeId,
    msgModuleAttributeJsmContactRequestTypeIdHelp,
    msgModuleAttributeJsmContactRequestTypeIdError,
  } = useLabels([
    'hlJsmContact',
    'labelModuleAttributeJsmContactDataKey',
    'placeholderModuleAttributeJsmContactDataKey',
    'msgModuleAttributeJsmContactDataKeyHelp',
    'msgModuleAttributeJsmContactDataKeyError',

    'labelModuleAttributeJsmContactPortalBaseUrl',
    'placeholderModuleAttributeJsmContactPortalBaseUrl',
    'msgModuleAttributeJsmContactPortalBaseUrlHelp',
    'msgModuleAttributeJsmContactPortalBaseUrlError',

    'labelModuleAttributeJsmContactRequestTypeId',
    'placeholderModuleAttributeJsmContactRequestTypeId',
    'msgModuleAttributeJsmContactRequestTypeIdHelp',
    'msgModuleAttributeJsmContactRequestTypeIdError',
  ]);

  const [dataKeyError, setDataKeyError] = useState<boolean>(false);
  const [portalBaseUrlError, setPortalBaseUrlError] = useState<boolean>(false);
  const [requestTypeIdError, setRequestTypeIdError] = useState<boolean>(false);

  // Handler: configuration
  const handleOnChange = useCallback(
    (config: JsmContactConfig) => (attr: keyof typeof config) => (value: (typeof config)[typeof attr]) => {
      let valid = true;
      switch (attr) {
        case 'dataKey':
          valid = value.match(/^[\da-f-]*$/g) !== null;
          setDataKeyError(!valid);
          break;
        case 'portalBaseUrl':
          valid = value === '' || value.match(/^(https?:\/\/)?[^/]+\/servicedesk\/customer\/portal\/\d+.*$/g) !== null;
          setPortalBaseUrlError(!valid);
          break;
        case 'requestTypeId':
          valid = value === '' || value.match(/^\d+$/g) !== null;
          setRequestTypeIdError(!valid);
          break;
      }
      if (valid) {
        const changedConfig = { ...config, [attr]: value };
        onChange('attributes.field_configuration', JSON.stringify(changedConfig));
      }
    },
    [onChange],
  );

  // Do not render if there is no data!
  if (!data) {
    return null;
  }

  // ClassName generation
  const clsModule = clsx({ ...className.module });
  const clsContainer = clsx({ ...className.container });

  // Get field values
  const fieldConfigValue: string = data.attributes.field_configuration || '';

  // derived values
  const jsmContactConfig = JsmContactConfigFactory(fieldConfigValue);
  const handleConfigFieldChange = handleOnChange(jsmContactConfig);
  const { dataKey, portalBaseUrl, requestTypeId } = jsmContactConfig;

  return (
    <div className={clsModule} id={getModuleUrlIdFromData(data.id, data.type)}>
      {editing ? (
        <div className={clsContainer}>
          <div className="row c-module__header">
            <div className="col u-margin-bottom-md container px-0">
              <div className="row">
                <h2>{hlJsmContact}</h2>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-12">
                  <FormElementText
                    value={dataKey}
                    onChange={handleConfigFieldChange('dataKey')}
                    label={labelModuleAttributeJsmContactDataKey}
                    placeholder={placeholderModuleAttributeJsmContactDataKey}
                    helpText={msgModuleAttributeJsmContactDataKeyHelp}
                  />
                  {dataKeyError && (
                    <div className="error c-hint--error text-sm">{msgModuleAttributeJsmContactDataKeyError}</div>
                  )}
                </div>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-12">
                  <FormElementText
                    value={portalBaseUrl}
                    onChange={handleConfigFieldChange('portalBaseUrl')}
                    label={labelModuleAttributeJsmContactPortalBaseUrl}
                    placeholder={placeholderModuleAttributeJsmContactPortalBaseUrl}
                    helpText={msgModuleAttributeJsmContactPortalBaseUrlHelp}
                  />
                  {portalBaseUrlError && (
                    <div className="error c-hint--error text-sm">{msgModuleAttributeJsmContactPortalBaseUrlError}</div>
                  )}
                </div>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-12">
                  <FormElementText
                    value={requestTypeId}
                    onChange={handleConfigFieldChange('requestTypeId')}
                    label={labelModuleAttributeJsmContactRequestTypeId}
                    placeholder={placeholderModuleAttributeJsmContactRequestTypeId}
                    helpText={msgModuleAttributeJsmContactRequestTypeIdHelp}
                  />
                  {requestTypeIdError && (
                    <div className="error c-hint--error text-sm">{msgModuleAttributeJsmContactRequestTypeIdError}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative px-0 col-12">
          <ContactForm
            dataKey={dataKey}
            portalBaseUrl={createPortalBaseUrl(portalBaseUrl)}
            requestTypeId={requestTypeId}
          />
        </div>
      )}
    </div>
  );
}
